import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import toast from "react-hot-toast";
import axios from "axios";
import PriceInput from "../components/PriceInput";
import {
  Plus,
  UserCog,
  Trash2,
  Edit,
  Banknote,
  Phone,
  Briefcase,
  User,
  Wallet,
  Users,
  Loader2,
  Printer,
  FileText,
  Shield,
  Lock,
} from "lucide-react";

const API = `${window.location.origin}/api`;

export default function EmployeesPage() {
  const { user } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    job_title: "",
    salary: "",
    address: "",
  });
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [payerName, setPayerName] = useState("");
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const [salaryCounter, setSalaryCounter] = useState(0);

  useEffect(() => {
    fetchEmployees();
    fetchSalaryCounter();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryCounter = async () => {
    try {
      const response = await axios.get(`${API}/employees/salary-counter`);
      setSalaryCounter(response.data.count || 0);
    } catch (error) {
      console.log("Counter not found, starting from 0");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        salary: parseFloat(formData.salary),
        salary_currency: "IQD",
      };
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, data);
        toast.success("تم تحديث بيانات الموظف بنجاح");
      } else {
        await axios.post(`${API}/employees`, data);
        toast.success("تم إضافة الموظف بنجاح");
      }
      fetchEmployees();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/employees/${selectedEmployee.id}/salary`, {
        employee_id: selectedEmployee.id,
        amount: parseFloat(paymentAmount),
        notes: paymentNotes,
        payer_name: payerName || user?.name || "المدير",
      });
      
      // حفظ بيانات الدفعة لعرض الوصل
      const newCounter = salaryCounter + 1;
      setLastPayment({
        receiptNumber: `R${String(newCounter).padStart(7, '0')}`,
        employeeName: selectedEmployee.name,
        jobTitle: selectedEmployee.job_title,
        amount: parseFloat(paymentAmount),
        payerName: payerName || user?.name || "المدير",
        notes: paymentNotes,
        date: new Date().toLocaleDateString('ar-IQ', { 
          year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit'
        }),
      });
      setSalaryCounter(newCounter);
      
      toast.success("تم صرف الراتب بنجاح");
      fetchEmployees();
      setIsPaymentDialogOpen(false);
      setShowReceipt(true);
      setPaymentAmount("");
      setPaymentNotes("");
      setPayerName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الموظف؟")) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("تم حذف الموظف");
      fetchEmployees();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      phone: employee.phone,
      job_title: employee.job_title,
      salary: employee.salary.toString(),
      address: employee.address || "",
      role_id: employee.role_id || "cashier",
      pin: employee.pin || "",
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingEmployee(null);
    setFormData({ name: "", phone: "", job_title: "", salary: "", address: "", role_id: "cashier", pin: "" });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ar-IQ").format(amount || 0) + " د.ع";
  };

  const openPaymentDialog = (employee) => {
    setSelectedEmployee(employee);
    setPaymentAmount(employee.salary.toString());
    setPayerName(user?.name || "");
    setIsPaymentDialogOpen(true);
  };

  const printReceipt = () => {
    if (!lastPayment) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>وصل صرف راتب - ${lastPayment.receiptNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Arial, sans-serif; 
            padding: 20px;
            max-width: 400px;
            margin: 0 auto;
          }
          .receipt {
            border: 2px solid #333;
            padding: 20px;
            border-radius: 10px;
          }
          .header {
            text-align: center;
            border-bottom: 2px dashed #333;
            padding-bottom: 15px;
            margin-bottom: 15px;
          }
          .header h1 {
            font-size: 24px;
            margin-bottom: 5px;
          }
          .header .receipt-num {
            font-size: 18px;
            color: #666;
            font-weight: bold;
          }
          .section {
            margin: 15px 0;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 5px;
          }
          .section-title {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          .section-value {
            font-size: 16px;
            font-weight: bold;
          }
          .amount-section {
            text-align: center;
            background: #e8f5e9;
            padding: 20px;
            border-radius: 10px;
            margin: 20px 0;
          }
          .amount {
            font-size: 32px;
            font-weight: bold;
            color: #2e7d32;
          }
          .signatures {
            display: flex;
            justify-content: space-between;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px dashed #333;
          }
          .signature-box {
            text-align: center;
            width: 45%;
          }
          .signature-line {
            border-top: 1px solid #333;
            margin-top: 40px;
            padding-top: 5px;
          }
          .date {
            text-align: center;
            color: #666;
            font-size: 12px;
            margin-top: 15px;
          }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <h1 style="font-size: 28px; color: #1a365d; margin-bottom: 10px;">${user?.shop_name || "اسم المؤسسة"}</h1>
            ${user?.phone ? `<div style="font-size: 14px; color: #666; margin-bottom: 10px;">📞 ${user.phone}</div>` : ''}
            <div style="border-top: 2px solid #1a365d; margin: 10px 0;"></div>
            <h2 style="font-size: 20px; margin-bottom: 5px;">وصل صرف راتب</h2>
            <div class="receipt-num">${lastPayment.receiptNumber}</div>
          </div>
          
          <div class="section">
            <div class="section-title">اسم الموظف</div>
            <div class="section-value">${lastPayment.employeeName}</div>
          </div>
          
          <div class="section">
            <div class="section-title">المسمى الوظيفي</div>
            <div class="section-value">${lastPayment.jobTitle}</div>
          </div>
          
          <div class="amount-section">
            <div class="section-title">المبلغ المستلم</div>
            <div class="amount">${formatCurrency(lastPayment.amount)}</div>
          </div>
          
          ${lastPayment.notes ? `
          <div class="section">
            <div class="section-title">ملاحظات</div>
            <div class="section-value">${lastPayment.notes}</div>
          </div>
          ` : ''}
          
          <div class="signatures">
            <div class="signature-box">
              <div class="section-title">المستلم</div>
              <div class="signature-line">${lastPayment.employeeName}</div>
            </div>
            <div class="signature-box">
              <div class="section-title">الصارف</div>
              <div class="signature-line">${lastPayment.payerName}</div>
            </div>
          </div>
          
          <div class="date">${lastPayment.date}</div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // حساب الإحصائيات
  const totalSalaries = employees.reduce((sum, emp) => sum + (emp.salary || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30">
              <Users className="w-6 h-6 text-red-400" />
            </div>
            إدارة الموظفين
          </h1>
          <p className="text-slate-400 mt-1">
            إجمالي الموظفين: {employees.length}
          </p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 shadow-lg shadow-red-600/25"
          data-testid="add-employee-btn"
        >
          <Plus className="w-5 h-5 me-2" />
          إضافة موظف جديد
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-500/20">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">عدد الموظفين</p>
              <p className="text-2xl font-bold text-white">{employees.length}</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-500/20">
              <Wallet className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">إجمالي الرواتب</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(totalSalaries)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Employees List */}
      {employees.length === 0 ? (
        <div className="text-center py-16 bg-slate-800/50 rounded-xl border border-slate-700">
          <UserCog className="w-20 h-20 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 text-lg mb-4">
            لا يوجد موظفين بعد
          </p>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 me-2" />
            أضف أول موظف
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {employees.map((employee) => (
            <Card
              key={employee.id}
              className="bg-slate-800/50 border-slate-700 hover:border-red-500/50 transition-all duration-300"
              data-testid={`employee-card-${employee.id}`}
            >
              <CardContent className="p-5">
                {/* Employee Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                      {employee.name?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{employee.name}</h3>
                      <p className="text-slate-400 text-sm flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {employee.job_title}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEdit(employee)}
                      className="text-slate-400 hover:text-amber-400 hover:bg-amber-500/10"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(employee.id)}
                      className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Contact Info */}
                {employee.phone && (
                  <div className="flex items-center gap-2 mb-4 text-slate-300">
                    <Phone className="w-4 h-4 text-slate-500" />
                    <span dir="ltr">{employee.phone}</span>
                  </div>
                )}

                {/* Salary Info */}
                <div className="p-4 rounded-xl bg-slate-900/50 border border-slate-700 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400 text-sm">الراتب الشهري</span>
                    <Wallet className="w-4 h-4 text-emerald-400" />
                  </div>
                  <p className="text-2xl font-bold text-emerald-400">
                    {formatCurrency(employee.salary)}
                  </p>
                </div>

                {/* Action Button */}
                <Button
                  onClick={() => openPaymentDialog(employee)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Banknote className="w-4 h-4 me-2" />
                  صرف راتب
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Employee Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <User className="w-5 h-5 text-red-400" />
              {editingEmployee ? "تعديل بيانات الموظف" : "إضافة موظف جديد"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                اسم الموظف
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="أدخل اسم الموظف"
                className="bg-slate-800 border-slate-700 text-white text-lg py-6"
                required
                data-testid="employee-name-input"
              />
            </div>

            {/* Job Title */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                المسمى الوظيفي
              </Label>
              <Input
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                placeholder="مثال: محاسب، مندوب مبيعات"
                className="bg-slate-800 border-slate-700 text-white text-lg py-6"
                required
                data-testid="employee-job-input"
              />
            </div>

            {/* Salary */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-400" />
                الراتب الشهري
              </Label>
              <PriceInput
                value={parseFloat(formData.salary) || 0}
                onChange={(v) => setFormData({ ...formData, salary: String(v) })}
                placeholder="0"
                testId="employee-salary-input"
                className="bg-slate-800 border-slate-700 text-white text-2xl font-bold py-6 text-center"
              />
              {formData.salary && (
                <p className="text-slate-400 text-sm text-center">
                  {new Intl.NumberFormat("ar-IQ").format(parseFloat(formData.salary) || 0)} دينار عراقي
                </p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                رقم الهاتف
              </Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="07XXXXXXXXX"
                className="bg-slate-800 border-slate-700 text-white text-lg py-6"
                dir="ltr"
                required
                data-testid="employee-phone-input"
              />
            </div>

            {/* الدور والصلاحيات */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <div>
                <Label className="text-slate-300 text-sm flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  الدور والصلاحيات
                </Label>
                <select
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="employee-role-select"
                >
                  <option value="cashier">🛒 كاشير</option>
                  <option value="salesman">💼 مندوب مبيعات</option>
                  <option value="inventory_manager">📦 أمين مخزن</option>
                  <option value="accountant">🧮 محاسب</option>
                  <option value="manager">👔 مدير</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-300 text-sm flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5 text-amber-500" />
                  الرمز السري (PIN)
                </Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={formData.pin}
                  onChange={(e) => setFormData({ ...formData, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  placeholder="مثال: 1234 (اختياري)"
                  className="mt-1 bg-slate-900 border-slate-700 text-white text-center font-mono tracking-widest"
                  maxLength={6}
                  data-testid="employee-pin-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">يطلبه الموظف ليبدأ ورديته</p>
              </div>
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                className="border-slate-600 text-slate-300 px-6"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 px-8"
                data-testid="save-employee-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  editingEmployee ? "حفظ التعديلات" : "إضافة الموظف"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-400" />
              صرف راتب
            </DialogTitle>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="p-4 rounded-xl bg-slate-800 border border-slate-700 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold">
                  {selectedEmployee.name?.charAt(0)}
                </div>
                <div>
                  <p className="text-white font-medium">{selectedEmployee.name}</p>
                  <p className="text-slate-400 text-sm">{selectedEmployee.job_title}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handlePayment} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-200">المبلغ</Label>
              <PriceInput
                value={parseFloat(paymentAmount) || 0}
                onChange={(v) => setPaymentAmount(String(v))}
                placeholder="0"
                testId="payment-amount-input"
                className="bg-slate-800 border-slate-700 text-white text-2xl font-bold py-6 text-center"
              />
              {paymentAmount && (
                <p className="text-slate-400 text-sm text-center">
                  {new Intl.NumberFormat("ar-IQ").format(parseFloat(paymentAmount) || 0)} دينار
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">اسم الصارف</Label>
              <Input
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
                placeholder="اسم الشخص الذي يقوم بالصرف"
                className="bg-slate-800 border-slate-700 text-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">ملاحظات (اختياري)</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="أي ملاحظات إضافية..."
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsPaymentDialogOpen(false)}
                className="border-slate-600 text-slate-300 px-6"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "تأكيد الصرف"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-400" />
              وصل صرف الراتب
            </DialogTitle>
          </DialogHeader>
          
          {lastPayment && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-slate-800 rounded-xl border border-slate-700">
                <p className="text-slate-400 text-sm">رقم الوصل</p>
                <p className="text-2xl font-bold text-emerald-400">{lastPayment.receiptNumber}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">المستلم</p>
                  <p className="text-white font-medium">{lastPayment.employeeName}</p>
                </div>
                <div className="p-3 bg-slate-800 rounded-lg">
                  <p className="text-slate-400 text-xs">الصارف</p>
                  <p className="text-white font-medium">{lastPayment.payerName}</p>
                </div>
              </div>
              
              <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
                <p className="text-slate-400 text-sm">المبلغ</p>
                <p className="text-3xl font-bold text-emerald-400">{formatCurrency(lastPayment.amount)}</p>
              </div>
              
              <div className="text-center text-slate-400 text-sm">
                {lastPayment.date}
              </div>
            </div>
          )}

          <DialogFooter className="gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowReceipt(false)}
              className="border-slate-600 text-slate-300"
            >
              إغلاق
            </Button>
            <Button
              onClick={printReceipt}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Printer className="w-4 h-4 me-2" />
              طباعة الوصل
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
